/**
 * KPI grande (26px/600) con etiqueta, valor y una de tres notas al pie: tendencia con flecha,
 * texto neutro o estado (punto + etiqueta). Se agrupan con `KpiStrip`.
 */
import type { CSSProperties, ReactNode } from 'react';
import styles from './KpiCard.module.css';

type Tone = 'default' | 'warning' | 'danger';

interface KpiCardProps {
  label: string;
  value: string;
  /** Tendencia ya formateada, p. ej. `▲ 4,2% este año`. */
  trend?: string;
  trendDirection?: 'up' | 'down' | 'flat';
  /** Nota neutra bajo el valor. */
  note?: string;
  /** Distintivo en píldora verde bajo el valor. */
  pill?: string;
  tone?: Tone;
}

const TONE_CLASS: Record<Tone, string> = {
  default: '',
  warning: styles.valueWarning!,
  danger: styles.valueDanger!,
};

const TREND_CLASS = {
  up: styles.trendUp!,
  down: styles.trendDown!,
  flat: styles.trendFlat!,
};

export function KpiCard({
  label,
  value,
  trend,
  trendDirection = 'up',
  note,
  pill,
  tone = 'default',
}: KpiCardProps) {
  return (
    <div className={styles.kpi}>
      <div className={styles.label}>{label}</div>
      <div className={`${styles.value} ${TONE_CLASS[tone]}`}>{value}</div>
      {trend ? (
        <div className={`${styles.trend} ${TREND_CLASS[trendDirection]}`}>{trend}</div>
      ) : null}
      {pill ? <div className={styles.pill}>{pill}</div> : null}
      {note ? <div className={styles.note}>{note}</div> : null}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  /** Unidad atenuada pegada al valor, p. ej. ` /mes`. */
  unit?: string;
  tone?: Tone;
}

/** KPI pequeño (24px/600), sin nota al pie. La segunda fila del panel. */
export function StatCard({ label, value, unit, tone = 'default' }: StatCardProps) {
  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>{label}</div>
      <div className={`${styles.statValue} ${TONE_CLASS[tone]}`}>
        {value}
        {unit ? <span className={styles.statUnit}>{unit}</span> : null}
      </div>
    </div>
  );
}

/** Métrica mínima (22px/800) de la pestaña Analítica. */
export function MetricCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.metric}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue}>{value}</div>
    </div>
  );
}

interface KpiStripProps {
  children: ReactNode;
  /** Número de cifras por fila en escritorio (por defecto 4). */
  columns?: number;
  /** Franja secundaria: menos relleno. */
  compact?: boolean;
  /** Última franja antes del contenido: más aire debajo. */
  last?: boolean;
  /** Ancla del recorrido guiado u otras marcas de prueba. */
  'data-tour'?: string;
}

/**
 * Franja de cifras: las tarjetas de KPI dentro de una sola superficie dividida por filetes.
 * Es la forma de «arriba lo importante en grande»: una lectura de un vistazo, no cuatro cajas.
 */
export function KpiStrip({ children, columns = 4, compact, last, ...rest }: KpiStripProps) {
  const className = [styles.strip, compact ? styles.stripCompact : '', last ? styles.stripLast : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={className}
      style={{ '--kpi-strip-cols': columns } as CSSProperties}
      data-tour={rest['data-tour']}
    >
      {children}
    </div>
  );
}
